import { Recipe } from '../adminApi';

/**
 * Generates SEO-optimized HTML for a recipe, ready to paste into Squarespace
 * Includes schema.org JSON-LD markup for Google rich snippets
 */
export function generateRecipeHtml(recipe: Recipe): string {
  const parts: string[] = [];

  // Schema.org JSON-LD for SEO
  parts.push(generateSchemaMarkup(recipe));
  parts.push('');

  // Note: Title is omitted - use Squarespace's native title field instead

  // Prep time and servings
  const servings = recipe.nutrition_info?.total_servings;
  if (recipe.prep_time || servings) {
    const metaParts: string[] = [];
    if (recipe.prep_time) metaParts.push(`Prep time: ${recipe.prep_time} minutes`);
    if (servings) metaParts.push(`Servings: ${servings}`);
    parts.push(`<p><em>${metaParts.join(' | ')}</em></p>`);
  }

  // Description
  if (recipe.description) {
    parts.push(`<p>${escapeHtml(recipe.description)}</p>`);
  }

  // Image with Pexels attribution
  if (recipe.image_url) {
    parts.push('');
    parts.push(`<img src="${escapeHtml(recipe.image_url)}" alt="${escapeHtml(recipe.name)}" style="max-width: 100%; height: auto; border-radius: 8px;" />`);

    // Pexels attribution (required by Pexels terms of service)
    if (recipe.image_photographer) {
      const photographerLink = recipe.image_photographer_url
        ? `<a href="${escapeHtml(recipe.image_photographer_url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(recipe.image_photographer)}</a>`
        : escapeHtml(recipe.image_photographer);
      parts.push(`<p><small>Photo by ${photographerLink} on <a href="https://www.pexels.com" target="_blank" rel="noopener noreferrer">Pexels</a></small></p>`);
    }
  }

  // Ingredients section
  if (recipe.ingredients && recipe.ingredients.length > 0) {
    parts.push('');
    parts.push('<h2>Ingredients</h2>');
    parts.push('<ul>');
    for (const ing of recipe.ingredients) {
      const quantity = formatQuantity(ing.quantity);
      const unit = ing.unit || '';
      const name = ing.name;
      parts.push(`  <li>${quantity}${unit ? ' ' + unit : ''} ${escapeHtml(name)}</li>`);
    }
    parts.push('</ul>');
  }

  // Instructions section
  if (recipe.steps && recipe.steps.length > 0) {
    parts.push('');
    parts.push('<h2>Instructions</h2>');
    parts.push('<ol>');
    for (const step of recipe.steps.sort((a, b) => a.step_number - b.step_number)) {
      if (step.header) {
        parts.push(`  <li><strong>${escapeHtml(step.header)}:</strong> ${escapeHtml(step.description)}</li>`);
      } else {
        parts.push(`  <li>${escapeHtml(step.description)}</li>`);
      }
    }
    parts.push('</ol>');
  } else if (recipe.instructions) {
    // Fallback to legacy instructions field
    parts.push('');
    parts.push('<h2>Instructions</h2>');
    parts.push(`<p>${escapeHtml(recipe.instructions).replace(/\n/g, '<br />')}</p>`);
  }

  // Nutrition section
  if (recipe.nutrition_info && Object.keys(recipe.nutrition_info).length > 0) {
    parts.push('');
    parts.push('<h2>Nutrition (per serving)</h2>');

    // Extract per_serving data if it exists, otherwise use root level
    const nutritionData = recipe.nutrition_info.per_serving || recipe.nutrition_info;

    // Filter out metadata/non-nutritional fields and only show primitive values
    const nutritionItems = Object.entries(nutritionData)
      .filter(([key, value]) => {
        // Skip nested objects and metadata fields
        if (typeof value === 'object') return false;
        if (['metadata', 'per_serving', 'total_servings'].includes(key)) return false;
        return true;
      })
      .map(([key, value]) => {
        // Format with units
        const formattedKey = capitalize(key.replace(/_/g, ' '));
        if (key === 'calories') return `${formattedKey}: ${value}`;
        if (key === 'sodium') return `${formattedKey}: ${value}mg`;
        return `${formattedKey}: ${value}g`;
      })
      .join(' | ');

    if (nutritionItems) {
      parts.push(`<p>${nutritionItems}</p>`);
    }
  }

  // Dietary tags (optional, helpful for readers)
  if (recipe.tags && recipe.tags.length > 0) {
    parts.push('');
    const tagNames = recipe.tags.map(t => t.tag_name).join(', ');
    parts.push(`<p><em>Dietary info: ${escapeHtml(tagNames)}</em></p>`);
  }

  return parts.join('\n');
}

/**
 * Generates schema.org Recipe JSON-LD markup
 */
function generateSchemaMarkup(recipe: Recipe): string {
  const schema: any = {
    '@context': 'https://schema.org',
    '@type': 'Recipe',
    name: recipe.name,
  };

  if (recipe.description) {
    schema.description = recipe.description;
  }

  if (recipe.image_url) {
    schema.image = recipe.image_url;
  }

  if (recipe.prep_time) {
    // ISO 8601 duration format
    schema.prepTime = `PT${recipe.prep_time}M`;
    schema.totalTime = `PT${recipe.prep_time}M`;
  }

  // Servings (recipeYield)
  if (recipe.nutrition_info?.total_servings) {
    schema.recipeYield = `${recipe.nutrition_info.total_servings} servings`;
  }

  // Ingredients as strings
  if (recipe.ingredients && recipe.ingredients.length > 0) {
    schema.recipeIngredient = recipe.ingredients.map(ing => {
      const quantity = formatQuantity(ing.quantity);
      const unit = ing.unit || '';
      return `${quantity}${unit ? ' ' + unit : ''} ${ing.name}`;
    });
  }

  // Instructions
  if (recipe.steps && recipe.steps.length > 0) {
    schema.recipeInstructions = recipe.steps
      .sort((a, b) => a.step_number - b.step_number)
      .map(step => ({
        '@type': 'HowToStep',
        text: step.description,
        ...(step.header && { name: step.header }),
      }));
  } else if (recipe.instructions) {
    schema.recipeInstructions = recipe.instructions;
  }

  // Nutrition
  if (recipe.nutrition_info) {
    const nutrition: any = { '@type': 'NutritionInformation' };
    const info = recipe.nutrition_info;

    if (info.calories) nutrition.calories = `${info.calories} calories`;
    if (info.protein) nutrition.proteinContent = `${info.protein}g`;
    if (info.carbs || info.carbohydrates) nutrition.carbohydrateContent = `${info.carbs || info.carbohydrates}g`;
    if (info.fats || info.fat) nutrition.fatContent = `${info.fats || info.fat}g`;
    if (info.fiber) nutrition.fiberContent = `${info.fiber}g`;
    if (info.sugar) nutrition.sugarContent = `${info.sugar}g`;
    if (info.sodium) nutrition.sodiumContent = `${info.sodium}mg`;

    if (Object.keys(nutrition).length > 1) {
      schema.nutrition = nutrition;
    }
  }

  // Dietary tags as keywords
  if (recipe.tags && recipe.tags.length > 0) {
    schema.keywords = recipe.tags.map(t => t.tag_name).join(', ');

    // Add suitableForDiet if applicable
    const dietMap: { [key: string]: string } = {
      'vegetarian': 'https://schema.org/VegetarianDiet',
      'vegan': 'https://schema.org/VeganDiet',
      'gluten-free': 'https://schema.org/GlutenFreeDiet',
      'dairy-free': 'https://schema.org/LactoseFreeDiet',
      'low-carb': 'https://schema.org/LowCarbohydrateDiet',
      'low-fat': 'https://schema.org/LowFatDiet',
      'kosher': 'https://schema.org/KosherDiet',
      'halal': 'https://schema.org/HalalDiet',
    };

    const diets = recipe.tags
      .map(t => dietMap[t.tag_name.toLowerCase()])
      .filter(Boolean);

    if (diets.length > 0) {
      schema.suitableForDiet = diets;
    }
  }

  return `<script type="application/ld+json">\n${JSON.stringify(schema, null, 2)}\n</script>`;
}

/**
 * Escape HTML special characters
 */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Format quantity - remove trailing zeros for cleaner display
 */
function formatQuantity(quantity: number): string {
  if (Number.isInteger(quantity)) {
    return quantity.toString();
  }
  // Handle common fractions
  const decimal = quantity % 1;
  const whole = Math.floor(quantity);

  const fractionMap: { [key: string]: string } = {
    '0.25': '¼',
    '0.33': '⅓',
    '0.5': '½',
    '0.67': '⅔',
    '0.75': '¾',
  };

  const roundedDecimal = decimal.toFixed(2);
  const fraction = fractionMap[roundedDecimal];

  if (fraction) {
    return whole > 0 ? `${whole} ${fraction}` : fraction;
  }

  return quantity.toString();
}

/**
 * Capitalize first letter
 */
function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}
